/**
 * receiptCapture.ts — Captura fiel del ticket web para PNG/PDF.
 *
 * Centraliza html2canvas con estilos monoespaciados, ancho estable y
 * reposicionamiento del nodo para clonar exactamente la vista en pantalla.
 */

const MONO_STACK =
  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace";

const CONTEXT_LABELS: Record<string, string> = {
  recibo: "Recibo_de_Compra",
  venta: "Recibo_de_Compra",
  cierre: "Cierre_de_Caja",
  arqueo: "Cierre_de_Caja",
  anulacion: "Anulacion_de_Venta",
  devolucion: "Devolucion_de_Venta",
};

/** Ancho estable del ticket web (max-w-md ≈ 28rem). */
export const TICKET_WEB_CAPTURE_WIDTH_PX = 448;

function mapDocContextLabel(docContext: string): string {
  const key = docContext.trim().toLowerCase().replace(/\s+/g, "_");
  if (CONTEXT_LABELS[key]) return CONTEXT_LABELS[key];
  return docContext
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_]/g, "");
}

/**
 * Nombre de archivo: `[Contexto]_[AAAA-MM-DD]_[HH-MM-SSam/pm].[ext]`
 * Devuelve basename sin extensión si `extension` está vacío.
 */
function generateDocName(
  docContext: string,
  extension: string,
  date: Date = new Date(),
): string {
  const mapped = mapDocContextLabel(docContext);
  const pad = (n: number) => String(n).padStart(2, "0");
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const h24 = date.getHours();
  const ampm = h24 >= 12 ? "pm" : "am";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const hh = pad(h12);
  const mi = pad(date.getMinutes());
  const ss = pad(date.getSeconds());
  const base = `${mapped}_${yyyy}-${mm}-${dd}_${hh}-${mi}-${ss}${ampm}`;
  const ext = extension.replace(/^\./, "").trim();
  return ext ? `${base}.${ext}` : base;
}

export function receiptBasename(
  docContext: string,
  date: Date = new Date(),
): string {
  return generateDocName(docContext, "", date);
}

/** Clon capturable en vista térmica: oculto visualmente; la captura fuerza opacity 1. */
export const TICKET_EXPORT_OFFSCREEN_CLASS =
  "fixed left-0 top-0 -z-[1] w-[28rem] max-w-full opacity-[0.02] pointer-events-none select-none force-light";

/** Clases del ticket visible en vista web — ancho fijo WYSIWYG con la exportación. */
export const TICKET_EXPORT_VISIBLE_CLASS =
  "w-[28rem] max-w-full mx-auto force-light";

function applyMonospaceTree(root: HTMLElement): void {
  root.style.fontFamily = MONO_STACK;
  root.querySelectorAll<HTMLElement>("*").forEach((node) => {
    node.style.fontFamily = MONO_STACK;
  });
}

function reinforceSolidRules(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>(".ticket-solid-rule").forEach((line) => {
    line.style.width = "100%";
    line.style.height = "1px";
    line.style.minHeight = "1px";
    line.style.backgroundColor = "#1f2937";
    line.style.display = "block";
  });

  root
    .querySelectorAll<HTMLElement>(".ticket-block-separator")
    .forEach((sep) => {
      sep.style.marginTop = "12px";
      sep.style.marginBottom = "12px";
    });
}

function reinforceTablePadding(root: HTMLElement): void {
  root
    .querySelectorAll<HTMLElement>(".ticket-cierre-grid-cell")
    .forEach((cell) => {
      cell.style.boxSizing = "border-box";
      cell.style.minHeight = "18px";
      cell.style.paddingTop = "4px";
      cell.style.paddingBottom = "4px";
      cell.style.lineHeight = "12px";
      cell.style.overflow = "visible";
    });

  root
    .querySelectorAll<HTMLElement>(".ticket-export-pago-cell")
    .forEach((cell) => {
      cell.style.paddingTop = "8px";
      cell.style.paddingBottom = "8px";
      cell.style.color = "#111827";
    });

  root
    .querySelectorAll<HTMLElement>(".ticket-export-pago-grid")
    .forEach((grid) => {
      grid.style.width = "100%";
      grid.style.display = "grid";
    });
}

function preserveTicketLayout(root: HTMLElement): void {
  root.classList.add("force-light");
  root.style.backgroundColor = "#ffffff";
  root.style.color = "#1f2937";

  root.querySelectorAll<HTMLElement>('[class*="text-center"]').forEach((el) => {
    el.style.textAlign = "center";
  });

  root.querySelectorAll<HTMLElement>(".flex").forEach((el) => {
    if (el.className.includes("items-center")) {
      el.style.alignItems = "center";
    }
    if (el.className.includes("justify-between")) {
      el.style.justifyContent = "space-between";
    }
    if (el.className.includes("flex-col")) {
      el.style.flexDirection = "column";
    }
  });

  root.querySelectorAll<HTMLElement>("table").forEach((table) => {
    table.style.width = "100%";
    table.style.borderCollapse = "collapse";
    table.style.tableLayout = "fixed";
  });

  root
    .querySelectorAll<HTMLElement>(".ticket-item-price, .font-black")
    .forEach((el) => {
      const hasSemanticColor = [
        "text-emerald-700",
        "text-green-700",
        "text-blue-700",
        "text-red-700",
      ].some((className) => el.classList.contains(className));
      if (!hasSemanticColor) el.style.color = "#111827";
    });
}

export interface ReceiptCaptureOptions {
  scale?: number;
  backgroundColor?: string;
  fixedWidthPx?: number;
}

export interface ReceiptCaptureResult {
  canvas: HTMLCanvasElement;
  scale: number;
  cssWidth: number;
  cssHeight: number;
}

function isWebExportRoot(el: HTMLElement): boolean {
  return el.hasAttribute("data-ticket-export-root");
}

function isThermalExportRoot(el: HTMLElement): boolean {
  return el.hasAttribute("data-thermal-export-root");
}

function resolveExportRoot(element: HTMLElement): HTMLElement {
  if (isWebExportRoot(element) || isThermalExportRoot(element)) return element;
  return (
    element.querySelector<HTMLElement>("[data-thermal-export-root]") ??
    element.querySelector<HTMLElement>("[data-ticket-export-root]") ??
    element
  );
}

function findClonedExportRoot(doc: Document): HTMLElement | null {
  return (
    doc.querySelector<HTMLElement>("[data-thermal-export-root]") ??
    doc.querySelector<HTMLElement>("[data-ticket-export-root]")
  );
}

function resolveCaptureScale(
  exportRoot: HTMLElement,
  override?: number,
): number {
  if (override && override > 0) return override;
  const attr = exportRoot.getAttribute("data-capture-scale");
  if (attr) {
    const parsed = Number.parseInt(attr, 10);
    if (parsed > 0) return parsed;
  }
  if (isWebExportRoot(exportRoot) || isThermalExportRoot(exportRoot)) return 3;
  return 2;
}

function resolveCaptureWidth(
  exportRoot: HTMLElement,
  override?: number,
): number | undefined {
  if (override && override > 0) return override;
  const attr = exportRoot.getAttribute("data-capture-width");
  if (attr) {
    const parsed = Number.parseInt(attr, 10);
    if (parsed > 0) return parsed;
  }
  if (!isWebExportRoot(exportRoot)) return undefined;
  const measured = Math.round(exportRoot.getBoundingClientRect().width);
  return measured > 0 ? measured : TICKET_WEB_CAPTURE_WIDTH_PX;
}

function reinforceThermalCapture(root: HTMLElement): void {
  root.style.opacity = "1";
  root.style.visibility = "visible";
  root.style.backgroundColor = "#fcfcfc";
  root.style.color = "#111111";
  root.style.overflow = "visible";
  root.style.height = "auto";
  root.style.maxHeight = "none";
  root.style.boxShadow = "none";

  root
    .querySelectorAll<HTMLElement>(
      ".thermal-sim-container, .thermal-sim-container > div",
    )
    .forEach((node) => {
      node.style.overflow = "visible";
      node.style.height = "auto";
      node.style.minHeight = "0";
      node.style.display = "block";
      node.style.whiteSpace = "pre";
    });
}

async function waitForPaint(): Promise<void> {
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

/**
 * Captura térmica WYSIWYG: clona la tira fuera del DOM apilado del modal,
 * sin hacks de export web (monospace global, overflow forzado, width lock).
 */
async function captureThermalCanvas(
  exportRoot: HTMLElement,
  scale: number,
  backgroundColor: string,
): Promise<ReceiptCaptureResult> {
  const { default: html2canvas } = await import("html2canvas");
  await document.fonts.ready;
  await waitForPaint();

  const rect = exportRoot.getBoundingClientRect();
  const renderWidth = Math.round(rect.width);
  if (renderWidth <= 0) {
    throw new Error("[captureThermalCanvas] ancho de tira térmica inválido");
  }

  const clone = exportRoot.cloneNode(true) as HTMLElement;
  const origContainer = exportRoot.querySelector<HTMLElement>(
    ".thermal-sim-container",
  );
  const cloneContainer = clone.querySelector<HTMLElement>(
    ".thermal-sim-container",
  );
  if (origContainer && cloneContainer) {
    cloneContainer.style.cssText = origContainer.style.cssText;
  }

  Object.assign(clone.style, {
    position: "fixed",
    top: "0",
    left: "0",
    width: `${renderWidth}px`,
    margin: "0",
    opacity: "1",
    visibility: "visible",
    zIndex: "-10000",
    pointerEvents: "none",
    overflow: "visible",
    height: "auto",
    maxHeight: "none",
    boxShadow: "none",
    borderRadius: "0",
    backgroundColor,
  });

  reinforceThermalCapture(clone);

  document.body.appendChild(clone);
  await waitForPaint();

  const captureHeight = Math.max(clone.scrollHeight, clone.offsetHeight, 1);

  try {
    const canvas = await html2canvas(clone, {
      scale,
      backgroundColor,
      useCORS: true,
      allowTaint: false,
      logging: false,
      foreignObjectRendering: true,
      scrollX: 0,
      scrollY: 0,
      x: 0,
      y: 0,
      width: renderWidth,
      height: captureHeight,
    });

    return {
      canvas,
      scale,
      cssWidth: renderWidth,
      cssHeight: Math.round(canvas.height / scale),
    };
  } finally {
    clone.remove();
  }
}

function reinforceCaptureVisibility(root: HTMLElement): void {
  root.style.opacity = "1";
  root.style.visibility = "visible";
  root.style.zIndex = "0";
  root.style.pointerEvents = "none";

  let parent = root.parentElement;
  while (parent) {
    parent.style.opacity = "1";
    parent.style.visibility = "visible";
    parent.style.overflow = "visible";
    parent = parent.parentElement;
  }
}

interface CaptureStyleSnapshot {
  opacity: string;
  visibility: string;
  zIndex: string;
  position: string;
  left: string;
  top: string;
  pointerEvents: string;
}

function snapshotCaptureStyles(el: HTMLElement): CaptureStyleSnapshot {
  return {
    opacity: el.style.opacity,
    visibility: el.style.visibility,
    zIndex: el.style.zIndex,
    position: el.style.position,
    left: el.style.left,
    top: el.style.top,
    pointerEvents: el.style.pointerEvents,
  };
}

function restoreCaptureStyles(
  el: HTMLElement,
  prev: CaptureStyleSnapshot,
): void {
  el.style.opacity = prev.opacity;
  el.style.visibility = prev.visibility;
  el.style.zIndex = prev.zIndex;
  el.style.position = prev.position;
  el.style.left = prev.left;
  el.style.top = prev.top;
  el.style.pointerEvents = prev.pointerEvents;
}

/** html2canvas necesita opacity 1 en el clon; ancla en viewport si hace falta. */
function prepareLiveNodeForCapture(exportRoot: HTMLElement): () => void {
  const prev = snapshotCaptureStyles(exportRoot);
  const rect = exportRoot.getBoundingClientRect();
  const needsAnchor =
    isWebExportRoot(exportRoot) ||
    rect.left < -200 ||
    rect.right > window.innerWidth + 200 ||
    rect.width <= 0;

  if (!needsAnchor) return () => undefined;

  exportRoot.style.position = "fixed";
  exportRoot.style.left = "0";
  exportRoot.style.top = "0";
  exportRoot.style.visibility = "visible";

  return () => restoreCaptureStyles(exportRoot, prev);
}

export async function captureReceiptCanvas(
  element: HTMLElement,
  opts: ReceiptCaptureOptions = {},
): Promise<ReceiptCaptureResult> {
  const exportRoot = resolveExportRoot(element);
  const scale = resolveCaptureScale(exportRoot, opts.scale);
  const backgroundColor = opts.backgroundColor ?? "#ffffff";
  const lockWidth = isWebExportRoot(exportRoot);
  const fixedWidthPx = lockWidth
    ? resolveCaptureWidth(exportRoot, opts.fixedWidthPx)
    : undefined;
  const thermalCapture = isThermalExportRoot(exportRoot);

  if (thermalCapture) {
    return captureThermalCanvas(exportRoot, scale, backgroundColor);
  }

  const { default: html2canvas } = await import("html2canvas");
  await document.fonts.ready;

  const restoreLiveCapture = prepareLiveNodeForCapture(exportRoot);
  const prevLiveOpacity = exportRoot.style.opacity;
  const hiddenExportRoot = isWebExportRoot(exportRoot) && !thermalCapture;
  if (hiddenExportRoot) {
    exportRoot.style.opacity = "1";
  }

  const prevWidth = exportRoot.style.width;
  const prevMinWidth = exportRoot.style.minWidth;
  const prevMaxWidth = exportRoot.style.maxWidth;
  if (fixedWidthPx) {
    exportRoot.style.width = `${fixedWidthPx}px`;
    exportRoot.style.minWidth = `${fixedWidthPx}px`;
    exportRoot.style.maxWidth = `${fixedWidthPx}px`;
  }

  try {
    const captureHeight = Math.max(
      element.scrollHeight,
      exportRoot.scrollHeight,
      element.offsetHeight,
    );

    const options = {
      scale,
      backgroundColor,
      useCORS: true,
      logging: false,
      ...(fixedWidthPx
        ? {
            width: fixedWidthPx,
            windowWidth: fixedWidthPx,
            windowHeight: captureHeight,
            scrollX: 0,
            scrollY: 0,
          }
        : {}),
      onclone: (doc: Document) => {
        const cloned = findClonedExportRoot(doc);
        if (!cloned || isThermalExportRoot(cloned)) return;

        reinforceCaptureVisibility(cloned);
        applyMonospaceTree(cloned);

        if (fixedWidthPx) {
          cloned.style.width = `${fixedWidthPx}px`;
          cloned.style.minWidth = `${fixedWidthPx}px`;
          cloned.style.maxWidth = `${fixedWidthPx}px`;
        }
        reinforceSolidRules(cloned);
        reinforceTablePadding(cloned);
        preserveTicketLayout(cloned);
      },
    };

    const canvas = await html2canvas(element, options);
    const cssWidth = fixedWidthPx ?? Math.round(canvas.width / scale);
    const cssHeight = Math.round(canvas.height / scale);

    return { canvas, scale, cssWidth, cssHeight };
  } finally {
    if (hiddenExportRoot) {
      exportRoot.style.opacity = prevLiveOpacity;
    }
    restoreLiveCapture();
    if (fixedWidthPx) {
      exportRoot.style.width = prevWidth;
      exportRoot.style.minWidth = prevMinWidth;
      exportRoot.style.maxWidth = prevMaxWidth;
    }
  }
}

/** Convierte px CSS a mm (96dpi estándar de html2canvas). */
export function cssPxToMm(px: number): number {
  return (px / 96) * 25.4;
}
